import React, { useId } from "react";
import { FiAlertCircle, FiLoader } from "react-icons/fi";

// The building blocks of every public form, so they all look and behave alike:
// a glowing card, fields with an icon, a label and an inline message, a submit button that shows
// progress, an alert, and a drawn-in tick for when it worked. The look itself lives in App.css (.fx-*).

export function FormCard({ title, subtitle, icon: Icon, children, className = "" }) {
  return (
    <div className={`fx-card ${className}`}>
      {(title || subtitle) && (
        <div className="flex items-start gap-3 mb-6">
          {Icon && (
            <span className="fx-card-icon shrink-0" aria-hidden="true">
              <Icon size={20} />
            </span>
          )}
          <div>
            {title && <h3 className="text-xl font-bold leading-tight">{title}</h3>}
            {subtitle && <p className="mt-1 text-sm text-gray-400">{subtitle}</p>}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

// One labelled field. `as` picks the element: "input" (default), "textarea" or "select" (give it <option>s).
export function Field({ label, icon: Icon, hint, error, required, as = "input", className = "", wrapperClass = "", ...rest }) {
  const id = useId();
  const Tag = as;
  const describedBy = error ? `${id}-error` : hint ? `${id}-hint` : undefined;
  return (
    <div className={`fx-field ${error ? "fx-field-error" : ""} ${wrapperClass}`}>
      {label && (
        <label htmlFor={id} className="fx-label">
          {label}
          {required && <span className="text-accent-light" aria-hidden="true"> *</span>}
        </label>
      )}
      <div className={`fx-control ${Icon ? "fx-has-icon" : ""} ${as === "textarea" ? "fx-control-area" : ""}`}>
        {Icon && <Icon className="fx-icon" size={16} aria-hidden="true" />}
        <Tag
          id={id}
          required={required}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={`fx-input ${className}`}
          {...rest}
        />
      </div>
      {error ? (
        <p id={`${id}-error`} role="alert" className="fx-msg text-red-400">{error}</p>
      ) : hint ? (
        <p id={`${id}-hint`} className="fx-msg text-gray-500">{hint}</p>
      ) : null}
    </div>
  );
}

export function SubmitButton({ busy = false, busyLabel = "Sending...", icon: Icon, children, className = "", ...rest }) {
  return (
    <button type="submit" disabled={busy} className={`fx-btn ${className}`} {...rest}>
      <span className="fx-btn-shine" aria-hidden="true" />
      {busy ? <FiLoader className="animate-spin" aria-hidden="true" /> : Icon ? <Icon aria-hidden="true" /> : null}
      <span>{busy ? busyLabel : children}</span>
    </button>
  );
}

export function FormAlert({ children, kind = "error", className = "" }) {
  if (!children) return null;
  const tone = kind === "error" ? "fx-alert-error" : "fx-alert-info";
  return (
    <div role={kind === "error" ? "alert" : "status"} className={`fx-alert ${tone} ${className}`}>
      <FiAlertCircle className="shrink-0 mt-0.5" aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

// The "it worked" panel: a tick that draws itself, a heading, a line of text and an optional button.
export function SuccessPanel({ title, children, action, onAction }) {
  return (
    <div className="fx-success text-center py-8" role="status">
      <svg className="fx-tick mx-auto" width="72" height="72" viewBox="0 0 72 72" aria-hidden="true">
        <circle className="fx-tick-ring" cx="36" cy="36" r="31" fill="none" strokeWidth="3" />
        <path className="fx-tick-mark" d="M22 37 l10 10 l18 -21" fill="none" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <h3 className="mt-5 text-2xl font-bold">{title}</h3>
      <div className="mt-2 text-gray-400">{children}</div>
      {action && (
        <button type="button" onClick={onAction} className="mt-6 px-6 py-2.5 rounded-full border border-navy-border text-gray-200 hover:border-accent-light hover:text-white transition-colors">
          {action}
        </button>
      )}
    </div>
  );
}
